/**
 * Video Product Carousel - JavaScript Controller
 * Unique Prefix: VideoProductCarousel
 */

class VideoProductCarousel {
  constructor(container) {
    this.container = container;
    this.currentSlide = 0;
    this.totalSlides = 0;
    this.isTransitioning = false;
    this.autoPlayInterval = null;
    this.useVideoEndForAutoplay = true; // Advance when video ends

    // Cache DOM elements
    this.track = container.querySelector(".vpc-carousel-slides-track");
    this.slides = container.querySelectorAll(".vpc-carousel-slide-item");
    this.video = container.querySelector(".vpc-background-video-element");
    this.prevBtn = container.querySelector(".vpc-nav-button-prev");
    this.nextBtn = container.querySelector(".vpc-nav-button-next");
    this.dotsContainer = container.querySelector(
      ".vpc-dots-indicator-container"
    );
    this.dots = container.querySelectorAll(".vpc-indicator-dot-button");

    this.totalSlides = this.slides.length;

    if (this.totalSlides === 0) return;

    this.init();
  }

  init() {
    this.bindEvents();
    this.updateCarousel(0, false);
    this.loadVideo(0);

    // Auto-play setup if enabled
    if (this.container.dataset.autoplay === "true") {
      this.startAutoPlay();
    }
  }

  bindEvents() {
    // Previous button
    if (this.prevBtn) {
      this.prevBtn.addEventListener("click", () => this.goToPrev());
    }

    // Next button
    if (this.nextBtn) {
      this.nextBtn.addEventListener("click", () => this.goToNext());
    }

    // Dot indicators
    this.dots.forEach((dot, index) => {
      dot.addEventListener("click", () => this.goToSlide(index));
    });

    // Touch/Swipe support
    this.setupTouchEvents();

    // Keyboard navigation
    this.container.addEventListener("keydown", (e) => this.handleKeydown(e));

    // Video events
    if (this.video) {
      this.video.addEventListener("loadeddata", () => {
        this.video.classList.add("vpc-video-visible");
      });

      // Auto-advance when video ends
      this.video.addEventListener("ended", () => {
        if (
          this.container.dataset.autoplay === "true" &&
          this.useVideoEndForAutoplay
        ) {
          this.goToNext();
        }
      });

      this.video.addEventListener("error", () => {
        console.warn("VideoProductCarousel: Video failed to load");
        // If video fails, still advance after interval
        if (this.container.dataset.autoplay === "true") {
          this.startAutoPlay();
        }
      });
    }

    // Pause autoplay on hover
    this.container.addEventListener("mouseenter", () => this.pauseAutoPlay());
    this.container.addEventListener("mouseleave", () => {
      if (this.container.dataset.autoplay === "true") {
        this.startAutoPlay();
      }
    });
  }

  setupTouchEvents() {
    let touchStartX = 0;
    let touchEndX = 0;
    const minSwipeDistance = 50;

    this.track.addEventListener(
      "touchstart",
      (e) => {
        touchStartX = e.changedTouches[0].screenX;
      },
      { passive: true }
    );

    this.track.addEventListener(
      "touchend",
      (e) => {
        touchEndX = e.changedTouches[0].screenX;
        const swipeDistance = touchEndX - touchStartX;

        if (Math.abs(swipeDistance) > minSwipeDistance) {
          if (swipeDistance > 0) {
            this.goToPrev();
          } else {
            this.goToNext();
          }
        }
      },
      { passive: true }
    );
  }

  handleKeydown(e) {
    switch (e.key) {
      case "ArrowLeft":
        this.goToPrev();
        break;
      case "ArrowRight":
        this.goToNext();
        break;
    }
  }

  goToPrev() {
    if (this.isTransitioning) return;

    const newIndex =
      this.currentSlide === 0 ? this.totalSlides - 1 : this.currentSlide - 1;

    this.goToSlide(newIndex);
  }

  goToNext() {
    if (this.isTransitioning) return;

    const newIndex =
      this.currentSlide === this.totalSlides - 1 ? 0 : this.currentSlide + 1;

    this.goToSlide(newIndex);
  }

  goToSlide(index) {
    if (this.isTransitioning || index === this.currentSlide) return;

    this.isTransitioning = true;
    const previousSlide = this.currentSlide;
    this.currentSlide = index;

    this.updateCarousel(index, true);
    this.loadVideo(index);

    // Reset transition lock
    setTimeout(() => {
      this.isTransitioning = false;
    }, 500);

    // Dispatch custom event
    this.container.dispatchEvent(
      new CustomEvent("vpc:slideChange", {
        detail: {
          currentSlide: index,
          previousSlide: previousSlide,
          totalSlides: this.totalSlides,
        },
      })
    );
  }

  updateCarousel(index, animate = true) {
    // Update track position
    if (this.track) {
      const translateX = -index * 100;
      this.track.style.transform = `translateX(${translateX}%)`;
    }

    // Update dots
    this.dots.forEach((dot, i) => {
      dot.classList.toggle("vpc-dot-active", i === index);
      dot.setAttribute("aria-selected", i === index ? "true" : "false");
    });

    // Update slides ARIA
    this.slides.forEach((slide, i) => {
      slide.setAttribute("aria-hidden", i !== index ? "true" : "false");
    });
  }

  loadVideo(index) {
    if (!this.video) {
      console.warn("VideoProductCarousel: No video element found");
      return;
    }

    const slide = this.slides[index];
    const videoUrl = slide?.dataset?.videoUrl;

    console.log(
      "VideoProductCarousel: Loading video for slide",
      index,
      "URL:",
      videoUrl
    );

    if (!videoUrl) {
      console.warn("VideoProductCarousel: No video URL found for slide", index);
      // If no video, auto-advance after interval
      if (this.container.dataset.autoplay === "true") {
        this.startAutoPlay();
      }
      return;
    }

    // Pause autoplay interval when video is loading (video end will trigger next)
    if (
      this.container.dataset.autoplay === "true" &&
      this.useVideoEndForAutoplay
    ) {
      this.pauseAutoPlay();
    }

    // Fade out current video
    this.video.classList.remove("vpc-video-visible");

    // Load new video after brief delay for smooth transition
    setTimeout(() => {
      // Remove all existing source elements
      const existingSources = this.video.querySelectorAll("source");
      existingSources.forEach((source) => source.remove());

      // Disable loop so 'ended' event fires for autoplay
      if (
        this.container.dataset.autoplay === "true" &&
        this.useVideoEndForAutoplay
      ) {
        this.video.loop = false;
      } else {
        this.video.loop = true;
      }

      // Create new source element
      const newSource = document.createElement("source");
      newSource.src = videoUrl;
      newSource.type = "video/mp4";
      this.video.appendChild(newSource);

      // Load and play the video
      this.video.load();

      // Add visible class when video is ready
      this.video.onloadeddata = () => {
        this.video.classList.add("vpc-video-visible");
      };

      this.video.play().catch((err) => {
        // Auto-play might be blocked, that's okay
        console.info("VideoProductCarousel: Autoplay blocked by browser", err);
        // If autoplay blocked, fall back to interval-based autoplay
        if (this.container.dataset.autoplay === "true") {
          this.startAutoPlay();
        }
      });
    }, 200);
  }

  startAutoPlay() {
    const interval = parseInt(this.container.dataset.autoplayInterval) || 5000;

    this.pauseAutoPlay(); // Clear any existing interval

    // Only use interval as fallback if video-end autoplay is disabled or video fails
    this.autoPlayInterval = setInterval(() => {
      this.goToNext();
    }, interval);
  }

  pauseAutoPlay() {
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.autoPlayInterval = null;
    }
  }

  destroy() {
    this.pauseAutoPlay();
    // Additional cleanup if needed
  }
}

// Initialize all carousels on page
function initVideoProductCarousels() {
  const carousels = document.querySelectorAll(
    ".vpc-video-product-carousel-section"
  );

  carousels.forEach((container) => {
    // Prevent double initialization
    if (container.dataset.vpcInitialized) return;
    container.dataset.vpcInitialized = "true";

    new VideoProductCarousel(container);
  });
}

// Initialize on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initVideoProductCarousels);
} else {
  initVideoProductCarousels();
}

// Re-initialize on Shopify section load/unload (for theme editor)
document.addEventListener("shopify:section:load", (e) => {
  const section = e.target;
  const carousel = section.querySelector(".vpc-video-product-carousel-section");

  if (carousel) {
    carousel.dataset.vpcInitialized = "";
    new VideoProductCarousel(carousel);
  }
});

document.addEventListener("shopify:section:unload", (e) => {
  const section = e.target;
  const carousel = section.querySelector(".vpc-video-product-carousel-section");

  if (carousel && carousel._vpcInstance) {
    carousel._vpcInstance.destroy();
  }
});

// Export for module usage
if (typeof module !== "undefined" && module.exports) {
  module.exports = VideoProductCarousel;
}
